import { Box, CustomTheme, Popover, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef } from 'react';
import type { PopoverActions } from '@mui/material/Popover';

type Props = {
  fab: ReactNode;
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  isFocusedModalOpen: boolean;
  positionKey?: string;
  onPositionKeyChange?: () => void;
  onPopoverPaperRef: (node: HTMLDivElement | null) => void;
  header: ReactNode;
  isHeaderScrolled?: boolean;
  children: ReactNode;
};

const FloatingPopoverContainer = ({
  fab,
  isOpen,
  anchorEl,
  onClose,
  isFocusedModalOpen,
  positionKey,
  onPositionKeyChange,
  onPopoverPaperRef,
  header,
  isHeaderScrolled = false,
  children,
}: Props): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const previousPositionKeyRef = useRef<string | undefined>(positionKey);
  const popoverActionsRef = useRef<PopoverActions | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const isPopoverInteractive = isOpen && !isFocusedModalOpen;

  useEffect(() => {
    const previousPositionKey = previousPositionKeyRef.current;
    if (previousPositionKey && positionKey && previousPositionKey !== positionKey) {
      onPositionKeyChange?.();
    }
    previousPositionKeyRef.current = positionKey;
  }, [onPositionKeyChange, positionKey]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const updatePopoverPosition = () => {
      if (resizeFrameRef.current != null) return;

      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        popoverActionsRef.current?.updatePosition();
      });
    };

    window.addEventListener('resize', updatePopoverPosition);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      if (resizeFrameRef.current != null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <Box>
      {fab}
      <Popover
        action={popoverActionsRef}
        open={isOpen}
        anchorEl={isOpen ? anchorEl : null}
        hideBackdrop
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        container={document.body}
        slotProps={{
          root: {
            keepMounted: true,
            disableAutoFocus: true,
            disableEnforceFocus: true,
            sx: {
              zIndex: isFocusedModalOpen ? theme.zIndex.modal - 1 : theme.zIndex.fab + 1,
              pointerEvents: 'none',
            },
          },
          paper: {
            ref: onPopoverPaperRef,
            sx: {
              width: 420,
              height: '75vh',
              maxHeight: '720px',
              borderRadius: '16px',
              padding: 0,
              ml: 1,
              mt: 1,
              position: 'relative',
              pointerEvents: isPopoverInteractive ? 'auto' : 'none',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              width: '100%',
              transition: 'border-bottom 0.1s',
              borderColor: 'divider',
              overflow: 'visible',
            }}>
            {header}
          </Box>
          <Box sx={{ width: '100%', flex: 1, minHeight: 0 }}>{children}</Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default FloatingPopoverContainer;
